/**
 * Ta mission — le bandeau « Étape N/5 » au-dessus du poste.
 *
 * Kayla, 06-10-2026 : une consigne visible à chaque étape. Le bandeau dit
 * trois choses et rien d'autre : OÙ il en est (le rail des cinq étapes), QUOI
 * faire maintenant (la consigne, en gros), et OÙ cliquer (le coup de pouce, en
 * petit dessous).
 *
 * ⚠️ Un temps passé est « passé », point — ni « réussi » ni « raté ». La scène
 * avance de force quand il patine ; afficher le verdict rendrait la note au
 * candidat alors qu'elle est à Kayla (décision du 18-08).
 *
 * 🪦 Le brief « 🎯 Ton boulot » qui se repliait au premier message est parti
 * le 06-10 : l'introduction (`IntroDuTest`) l'a remplacé, et l'onglet Aide en
 * garde la copie à portée de main.
 *
 * ⚠️ RÉGION À PART, pas un enfant de « La conversation » : le banc scope ses
 * requêtes sur le fil, et y glisser la consigne les rendrait ambiguës.
 */
import { Badge, Carte, Infobulle } from "@/shared/ui";
import { Check } from "@/shared/ui/icons";

import {
  LIBELLES_ETAPES,
  NOMBRE_D_ETAPES,
  NUMERO_ETAPE,
  TITRES_DU_RAIL,
  coupDePouce,
  type Etape,
} from "./scenario";

export function TaMission({ etape }: { etape: Etape }) {
  const numero = etape === "fini" ? NOMBRE_D_ETAPES + 1 : NUMERO_ETAPE[etape];
  const pouce = coupDePouce(etape);

  return (
    <Carte remplissage="md" role="region" aria-label="Ta mission" className="shrink-0">
      {/* Le rail des cinq étapes : le passé en vert (validé), le présent à la
          marque (c'est CE qu'on fait), le futur en gris. */}
      <ol className="flex min-w-0 items-center gap-2">
        {TITRES_DU_RAIL.map((titre, i) => {
          const n = i + 1;
          const passe = n < numero;
          const cEstIci = n === numero;
          return (
            <Infobulle key={titre} texte={titre}>
              <li aria-label={titre} aria-current={cEstIci ? "step" : undefined} className="min-w-0 flex-1">
                <Badge
                  size="md"
                  ton={passe ? "vert" : cEstIci ? "marque" : "gris"}
                  icon={passe ? <Check aria-hidden /> : undefined}
                  className="w-full justify-center"
                >
                  {!passe && n}
                  <span className="hidden truncate min-[1200px]:inline">{titre}</span>
                </Badge>
              </li>
            </Infobulle>
          );
        })}
      </ol>

      <div className="mt-3">
        <p className="text-[17px] font-semibold leading-snug text-fg">
          {etape === "fini" ? (
            "C'est fini — merci !"
          ) : (
            <>
              <span className="text-muted">
                Étape {numero}/{NOMBRE_D_ETAPES} :{" "}
              </span>
              {LIBELLES_ETAPES[etape]}
            </>
          )}
        </p>
        {pouce && (
          <p className="mt-1 text-[14px] leading-snug text-muted">
            <span aria-hidden className="font-bold text-fg">
              ➜{" "}
            </span>
            {pouce.texte}
          </p>
        )}
      </div>
    </Carte>
  );
}
